import React, { useState, useEffect } from 'react'
import { Button, message } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { updateProtocolInfographic } from '../../../../api/assessmentReview'
import styles from './ExecutiveSummary.module.css'
import ExecutiveSummaryHeader from './ExecutiveSummaryHeader'
import ExecutiveSummaryMetrics from './ExecutiveSummaryMetrics'
import ExecutiveSummaryWeekly from './ExecutiveSummaryWeekly'
import ExecutiveSummaryPillar from './ExecutiveSummaryPillar'
import ExecutiveSummaryEditor from './ExecutiveSummaryEditor'

export { default as ExecutiveSummaryHeader } from './ExecutiveSummaryHeader'
export { default as ExecutiveSummaryMetrics } from './ExecutiveSummaryMetrics'
export { default as ExecutiveSummaryWeekly } from './ExecutiveSummaryWeekly'
export { default as ExecutiveSummaryPillar } from './ExecutiveSummaryPillar'

const ExecutiveSummary = (props) => {
  const data = props.data || {}
  const overall = data.overall_assessment || null
  const editabled = props.editabled || false
  const onUpdate = props.onUpdate || (() => {})
  const clientId = props.clientId
  const assessmentId = props.assessmentId
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState({})
  const [displayData, setDisplayData] = useState(data)
  const [saving, setSaving] = useState(false)

  // Update displayData when props.data changes (e.g., after parent re-fetches)
  useEffect(() => {
    setDisplayData(data)
  }, [data])

  // Don't render if no data at all
  if (!data || Object.keys(data).length === 0) {
    return null
  }

  const handleEdit = () => {
    setEditData(displayData)
    setEditing(true)
  }

  const handleSave = async () => {
    if (!clientId || !assessmentId) {
      message.error('Missing client or assessment ID')
      return
    }

    setSaving(true)
    try {
      await updateProtocolInfographic(clientId, assessmentId, 'executive_summary', editData)
      onUpdate(editData)
      setDisplayData(editData) // Update local display data immediately
      message.success('Executive summary updated successfully!')
      setEditing(false)
    } catch (error) {
      message.error('Failed to save changes. Please try again.')
      console.error('Error saving executive summary:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditData(displayData)
    setEditing(false)
  }

  const handlePillarUpdate = (pillarKey, updatedPillarData) => {
    const updated = { ...displayData, [pillarKey]: updatedPillarData }
    setEditData(updated)
    onUpdate(updated)
  }

  const handleOverallUpdate = (updatedData) => {
    setEditData(updatedData)
    onUpdate(updatedData)
  }

  if (editing) {
    return (
      <ExecutiveSummaryEditor
        data={editData}
        onChange={setEditData}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        clientId={clientId}
        assessmentId={assessmentId}
      />
    )
  }

  return (
    <div className={styles.container}>
      {/* Edit Button */}
      {editabled && (
        <div style={{ textAlign: 'right', marginBottom: '12px' }}>
          <Button
            type="text"
            onClick={handleEdit}
            icon={<EditOutlined />}
            style={{ color: '#264382' }}
          >
            Refine with AI
          </Button>
        </div>
      )}

      {/* Header - Overall Assessment */}
      {displayData.overall_assessment && <ExecutiveSummaryHeader overall={displayData.overall_assessment} />}

      {/* Key Metrics to Track */}
      {displayData.key_metrics_to_track && (
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>
            <span role="img" aria-label="trophy">🏆</span>
            Key Metrics to Improve — Current vs Target
          </h2>
          <ExecutiveSummaryMetrics metrics={displayData.key_metrics_to_track} />
        </div>
      )}

      {/* Weekly Priorities & Focus */}
      {displayData.weekly_structure && (
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>
            <span role="img" aria-label="calendar">📅</span>
            Weekly Priorities & Focus
          </h2>
          <ExecutiveSummaryWeekly weeklyStructure={displayData.weekly_structure} />
        </div>
      )}

      {/* Three Pillars */}
      {(displayData.exercise || displayData.nutrition || displayData.sleep) && (
        <div className={styles.pillarsGrid}>
          {displayData.exercise && (
            <ExecutiveSummaryPillar 
              pillarKey="exercise" 
              title="Exercise" 
              pillarData={displayData.exercise}
            />
          )}
          {displayData.nutrition && (
            <ExecutiveSummaryPillar 
              pillarKey="nutrition" 
              title="Nutrition" 
              pillarData={displayData.nutrition}
            />
          )}
          {displayData.sleep && (
            <ExecutiveSummaryPillar 
              pillarKey="sleep" 
              title="Sleep" 
              pillarData={displayData.sleep}
            />
          )}
        </div>
      )}

      {/* Next Steps */}
      {displayData.next_steps && displayData.next_steps.length > 0 && (
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>
            <span role="img" aria-label="target">🎯</span>
            Next Steps — Prioritized Actions
          </h2>
          <ol className={styles.nextStepsList}>
            {displayData.next_steps.map((step, idx) => (
              <li key={idx} className={styles.nextStepItem}>
                <span
                  className={styles.nextStepNumber}
                  style={{ backgroundColor: ['#f5222d', '#faad14', '#52c41a', '#1677ff'][idx] || '#1677ff' }}
                >
                  {idx + 1}
                </span>
                {step}
                <div className={styles.nextStepMeta}>Start within 48 hours · Review in 7 days</div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

export default ExecutiveSummary
